import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useApp } from "@/context/AppContext";

const SITE_NAME = "BuyHeda";
// Canonical production domain. All canonical/hreflang/OG URLs point here regardless
// of which host (preview or production) renders the SPA, so search engines always
// index the real buyheda.com pages.
export const SITE_URL = "https://buyheda.com";

const upsertMeta = (attr, key, content) => {
  const head = document.head;
  let el = head.querySelector(`meta[${attr}="${key}"]`);
  if (content == null || content === "") {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    head.appendChild(el);
  }
  el.setAttribute("content", content);
};

// Manage all JSON-LD blocks in one place. Accepts an array of schema objects and
// re-renders them fresh on every route so no stale structured data leaks across pages.
const setJsonLd = (items) => {
  document.head.querySelectorAll("script[data-seo-jsonld]").forEach((el) => el.remove());
  (items || []).filter(Boolean).forEach((data, i) => {
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.setAttribute("data-seo-jsonld", String(i));
    el.textContent = JSON.stringify(data);
    document.head.appendChild(el);
  });
};

const upsertLink = (rel, href, hreflang) => {
  const head = document.head;
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]:not([hreflang])`;
  let el = head.querySelector(selector);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    if (hreflang) el.setAttribute("hreflang", hreflang);
    head.appendChild(el);
  }
  el.setAttribute("href", href);
};

/**
 * Declarative SEO head manager for the SPA.
 * Sets a single <title>, meta description, robots, Open Graph, Twitter Card,
 * one canonical URL, hreflang alternates (en / ru / x-default) and <html lang>.
 *
 * Canonical strategy (bilingual, language via ?lang= query):
 *  - English is the default and uses the clean URL (no lang param).
 *  - Russian uses ?lang=ru.
 *  - `canonicalPath` lets a page whitelist meaningful params (e.g. ?category=bags)
 *    while stripping noise (search q, filters, sort). Defaults to pathname only.
 */
export const Seo = ({
  title,
  description,
  image,
  type = "website",
  noindex = false,
  jsonLd = null,
  canonicalPath,
}) => {
  const { lang } = useApp();
  const { pathname } = useLocation();

  useEffect(() => {
    const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
    const img = image || `${SITE_URL}/og-image.jpg`;
    const cleanPath = canonicalPath || pathname; // may already include ?category=...
    const enUrl = `${SITE_URL}${cleanPath}`;
    const ruUrl = `${enUrl}${cleanPath.includes("?") ? "&" : "?"}lang=ru`;
    const canonical = lang === "ru" ? ruUrl : enUrl;

    document.title = fullTitle;
    document.documentElement.lang = lang;

    upsertMeta("name", "description", description);
    upsertMeta("name", "robots", noindex ? "noindex, nofollow" : "index, follow");

    // Open Graph
    upsertMeta("property", "og:site_name", SITE_NAME);
    upsertMeta("property", "og:title", fullTitle);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:url", canonical);
    upsertMeta("property", "og:image", img);
    upsertMeta("property", "og:locale", lang === "ru" ? "ru_RU" : "en_US");
    upsertMeta("property", "og:locale:alternate", lang === "ru" ? "en_US" : "ru_RU");

    // Twitter Card
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", fullTitle);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", img);

    // Canonical + hreflang alternates (self-referencing per language)
    upsertLink("canonical", canonical);
    upsertLink("alternate", enUrl, "en");
    upsertLink("alternate", ruUrl, "ru");
    upsertLink("alternate", enUrl, "x-default");

    // Structured data (JSON-LD) — accept a single object or an array
    setJsonLd(Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : []);
  }, [title, description, image, type, noindex, lang, pathname, jsonLd, canonicalPath]);

  // Clear structured data on unmount to avoid stale schema across routes
  useEffect(() => () => setJsonLd([]), []);

  return null;
};

export default Seo;
